import { storageMetadataService } from 'common/infrastructure/storage/services/StorageMetadataService';
import loggerService, { LoggerLevel } from 'common/infrastructure/log';
import { WORK_TASK_CACHE_CONFIG } from './work-task-cache-config';
import { Tp3Id } from '@planitgeo/treeplotter-domain/lib/domain/types';
import {
  compareHLCTimestamp,
  sortKeyToHLC,
} from '@planitgeo/treeplotter-domain/lib/infrastructure/clock/hybrid-logical-clock';

export interface WorkTaskEntity {
  id: Tp3Id;
  status: string | null;
  hlcTimestamp: string;
}

interface WorkTaskStorageConfig {
  // Storage thresholds (percentage of device quota)
  maxStorageUtilizationPercent: number;
  criticalStorageUtilizationPercent: number;

  // Minimum free space to maintain (bytes)
  minAvailableBytes: number;

  // Fallback count limit (when Storage API unavailable)
  maxTasks: number;

  // Eviction tuning
  evictionBatchSize: number;
  criticalEvictionBatchSize: number;
}

export type StorageAnalysis =
  | {
      shouldEvict: false;
      workTaskCount: number;
      storageUtilizationPercent?: number;
      availableBytes?: number;
    }
  | {
      shouldEvict: true;
      reason: 'quota_exceeded' | 'low_available' | 'task_count';
      severity: 'warning' | 'critical';
      workTaskCount: number;
      storageUtilizationPercent?: number;
      availableBytes?: number;
    };

export interface EvictionResult {
  evictedIds: Tp3Id[];
  evictedCount: number;
  remainingCount: number;
  oldestEvictedTimestamp?: string;
  newestEvictedTimestamp?: string;
}

export class WorkTaskCacheManager {
  private config: WorkTaskStorageConfig;

  constructor(config: WorkTaskStorageConfig) {
    this.config = config;
  }

  /**
   * Analyze storage state using device-level metrics from StorageMetadataService.
   * Determines if eviction is needed based on:
   * 1. Device storage quota utilization
   * 2. Available storage space
   * 3. Fallback task count limit
   */
  analyzeStorage(workTasks: WorkTaskEntity[]): StorageAnalysis {
    const storageState = storageMetadataService.getStorageMetadata();
    const workTaskCount = workTasks.length;

    let storageUtilizationPercent: number | undefined;
    let availableBytes: number | undefined;

    // Check 1: Device storage quota utilization
    if (storageState.quota && storageState.quota > 0 && storageState.usage) {
      const utilizationPercent = (storageState.usage / storageState.quota) * 100;
      storageUtilizationPercent = utilizationPercent;

      if (utilizationPercent >= this.config.criticalStorageUtilizationPercent) {
        return {
          shouldEvict: true,
          reason: 'quota_exceeded',
          severity: 'critical',
          workTaskCount,
          storageUtilizationPercent,
          availableBytes,
        };
      }

      if (utilizationPercent >= this.config.maxStorageUtilizationPercent) {
        return {
          shouldEvict: true,
          reason: 'quota_exceeded',
          severity: 'warning',
          workTaskCount,
          storageUtilizationPercent,
          availableBytes,
        };
      }
    }

    // Check 2: Available storage space
    if (storageState.available != null) {
      availableBytes = storageState.available;

      if (storageState.available < this.config.minAvailableBytes) {
        return {
          shouldEvict: true,
          reason: 'low_available',
          severity:
            storageState.available < this.config.minAvailableBytes / 2 ? 'critical' : 'warning',
          workTaskCount,
          storageUtilizationPercent,
          availableBytes,
        };
      }
    }

    // Check 3: Fallback - task count limit (for devices without Storage API)
    if (workTaskCount > this.config.maxTasks) {
      return {
        shouldEvict: true,
        reason: 'task_count',
        severity: 'warning',
        workTaskCount,
        storageUtilizationPercent,
        availableBytes,
      };
    }

    return {
      shouldEvict: false,
      workTaskCount,
      storageUtilizationPercent,
      availableBytes,
    };
  }

  /**
   * Plan FIFO eviction based on hlcTimestamp.
   * Evicts completed tasks first, then open tasks if needed to reach batch size.
   */
  planEviction(allTasks: WorkTaskEntity[], storageAnalysis: StorageAnalysis): EvictionResult {
    if (!storageAnalysis.shouldEvict || !Array.isArray(allTasks) || allTasks.length === 0) {
      return {
        evictedIds: [],
        evictedCount: 0,
        remainingCount: Array.isArray(allTasks) ? allTasks.length : 0,
      };
    }

    const batchSize =
      storageAnalysis.severity === 'critical'
        ? this.config.criticalEvictionBatchSize
        : this.config.evictionBatchSize;

    const completedTasks = allTasks
      .filter((task) => task.status?.toLowerCase() === 'completed' && task.hlcTimestamp)
      .sort((a, b) =>
        compareHLCTimestamp(sortKeyToHLC(a.hlcTimestamp), sortKeyToHLC(b.hlcTimestamp))
      );

    let tasksToEvict = completedTasks.slice(0, batchSize);

    // If batch size not reached, evict open tasks to meet quota
    const remainingBatchSize = batchSize - tasksToEvict.length;
    if (remainingBatchSize > 0) {
      const openTasks = allTasks
        .filter((task) => task.status?.toLowerCase() !== 'completed' && task.hlcTimestamp)
        .sort((a, b) =>
          compareHLCTimestamp(sortKeyToHLC(a.hlcTimestamp), sortKeyToHLC(b.hlcTimestamp))
        )
        .slice(0, remainingBatchSize);

      if (openTasks.length > 0) {
        loggerService.log(LoggerLevel.DEBUG, 'Evicting open tasks to reach batch size', {
          batchSize,
          completedEvicted: tasksToEvict.length,
          openEvicted: openTasks.length,
        });
        tasksToEvict = [...tasksToEvict, ...openTasks];
      }
    }

    return {
      evictedIds: tasksToEvict.map((t) => t.id),
      evictedCount: tasksToEvict.length,
      remainingCount: allTasks.length - tasksToEvict.length,
      oldestEvictedTimestamp: tasksToEvict[0]?.hlcTimestamp,
      newestEvictedTimestamp: tasksToEvict[tasksToEvict.length - 1]?.hlcTimestamp,
    };
  }
}

// Singleton instance with runtime configuration
export const workTaskCacheManager = new WorkTaskCacheManager(WORK_TASK_CACHE_CONFIG);
